import { type FormEvent, useEffect, useState } from 'react';
import { i18n } from '@wms/shared';
import { api } from '../lib/api.js';
import { formatDate } from '../lib/format.js';

const { t } = i18n;

const ROLES = ['CUSTOMER', 'PARTNER_ADMIN', 'ORDER_PLACER', 'OPERATOR', 'ACCOUNTANT', 'ADMIN'] as const;
type RoleStr = (typeof ROLES)[number];

const SCOPES = ['NONE', 'STORAGE_CUSTOMER', 'PARTNER'] as const;
type ScopeStr = (typeof SCOPES)[number];

interface InvitationRow {
  id: string;
  email: string;
  displayName: string;
  role: RoleStr;
  scopeType: ScopeStr;
  scopeId: string | null;
  status: 'PENDING' | 'ACCEPTED' | 'REVOKED' | 'EXPIRED';
  invitedAt: string;
  expiresAt: string;
}

const STATUS_CLASS: Record<InvitationRow['status'], string> = {
  PENDING: 'bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-900/40 dark:text-amber-100',
  ACCEPTED:
    'bg-emerald-100 text-emerald-900 border-emerald-300 dark:bg-emerald-900/40 dark:text-emerald-100',
  REVOKED: 'bg-muted text-muted-foreground border-border',
  EXPIRED: 'bg-red-100 text-red-900 border-red-300 dark:bg-red-900/40 dark:text-red-100',
};

export function InvitationsPage() {
  const [rows, setRows] = useState<InvitationRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<RoleStr>('ORDER_PLACER');
  const [scopeType, setScopeType] = useState<ScopeStr>('PARTNER');
  const [scopeId, setScopeId] = useState('');
  const [busy, setBusy] = useState(false);
  const [emailWarning, setEmailWarning] = useState<string | null>(null);

  useEffect(() => {
    setRows(null);
    api<InvitationRow[]>('/invitations')
      .then(setRows)
      .catch((err: unknown) => setError(String(err)));
  }, [reload]);

  async function invite(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setEmailWarning(null);
    try {
      const res = await api<{ emailDelivered?: boolean; emailError?: string | null }>(
        '/invitations',
        {
          method: 'POST',
          body: {
            email,
            displayName: name,
            role,
            scopeType,
            scopeId: scopeId.trim() ? scopeId.trim() : null,
          },
        },
      );
      // Invitation is saved even if the email send failed — warn so the admin
      // knows the recipient won't get a link automatically.
      if (res?.emailDelivered === false) {
        setEmailWarning(
          t('invitations.email_failed').replace('{{reason}}', res.emailError ?? ''),
        );
      }
      setEmail('');
      setName('');
      setScopeId('');
      setReload((k) => k + 1);
    } catch {
      setError(t('common.error'));
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: string) {
    if (!window.confirm(t('common.confirm_delete'))) return;
    try {
      await api(`/invitations/${id}`, { method: 'DELETE' });
      setReload((k) => k + 1);
    } catch {
      setError(t('common.error'));
    }
  }

  return (
    <section className="space-y-4">
      <h2 className="text-xl font-semibold">{t('invitations.title')}</h2>

      <form onSubmit={(e) => void invite(e)} className="rounded border border-border p-4 space-y-2 text-sm max-w-3xl">
        <h3 className="font-semibold">{t('invitations.invite_new')}</h3>
        <div className="grid grid-cols-2 gap-2">
          <label className="block space-y-1">
            <span className="font-medium">{t('auth.login.email')}</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full rounded border border-border bg-background px-2 py-1.5"
            />
          </label>
          <label className="block space-y-1">
            <span className="font-medium">{t('invitation.display_name')}</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full rounded border border-border bg-background px-2 py-1.5"
            />
          </label>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <label className="block space-y-1">
            <span className="font-medium">{t('invitations.role')}</span>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as RoleStr)}
              className="w-full rounded border border-border bg-background px-2 py-1.5"
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </label>
          <label className="block space-y-1">
            <span className="font-medium">{t('invitations.scope_type')}</span>
            <select
              value={scopeType}
              onChange={(e) => setScopeType(e.target.value as ScopeStr)}
              className="w-full rounded border border-border bg-background px-2 py-1.5"
            >
              {SCOPES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <label className="block space-y-1">
            <span className="font-medium">{t('invitations.scope_id')}</span>
            <input
              value={scopeId}
              onChange={(e) => setScopeId(e.target.value)}
              placeholder={t('invitations.scope_id_hint')}
              className="w-full rounded border border-border bg-background px-2 py-1.5 font-mono text-xs"
            />
          </label>
        </div>
        {error ? <div className="text-red-600">{error}</div> : null}
        {emailWarning ? (
          <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-100">
            {emailWarning}
          </div>
        ) : null}
        <button
          type="submit"
          disabled={busy || !email || !name}
          className="rounded bg-primary text-primary-foreground px-3 py-1.5 disabled:opacity-50"
        >
          {busy ? t('common.loading') : t('invitations.send')}
        </button>
      </form>

      {rows === null ? (
        <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
      ) : rows.length === 0 ? (
        <div className="rounded border border-dashed border-border p-4 text-sm text-muted-foreground">
          {t('invitations.empty')}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left">
              <tr>
                <th scope="col" className="px-3 py-2 font-medium">{t('auth.login.email')}</th>
                <th scope="col" className="px-3 py-2 font-medium">{t('invitations.role')}</th>
                <th scope="col" className="px-3 py-2 font-medium">{t('invitations.scope_type')}</th>
                <th scope="col" className="px-3 py-2 font-medium">{t('invitations.invited_at')}</th>
                <th scope="col" className="px-3 py-2 font-medium">{t('billing.col.status')}</th>
                <th scope="col" className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="px-3 py-2">{r.email}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.role}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.scopeType}</td>
                  <td className="px-3 py-2 text-xs">{formatDate(r.invitedAt)}</td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${STATUS_CLASS[r.status]}`}
                    >
                      {r.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {r.status === 'PENDING' ? (
                      <button
                        type="button"
                        onClick={() => void revoke(r.id)}
                        aria-label={t('common.delete')}
                        className="rounded border border-border px-2 py-1 text-xs"
                      >
                        ×
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
